<template>
    <b-card>
        <b-row align-h="center">
            <span :class="showInputs ? '' : 'selected'">Results</span>
            <b-form-checkbox switch size="lg" class="spacing" v-model="showInputs"></b-form-checkbox>
            <span :class="showInputs ? 'selected' : ''">Inputs</span>
        </b-row>
        <b-row align-h="center">
            <b-col cols="3" class="mx-auto">
                <b-form-group label="Scenario 1" label-align="left" class="mt-1">
                    <b-form-select
                        :value="getTimeStringFromScenario('compareScenario1')"
                        :options="getCompareOptionsTimes"
                        size="md"
                        @input="getCompareScenario('compareScenario1', $event);"
                        :disabled="getCompareOptions.length <= 0"
                        data-test="compareSelect1"
                    ></b-form-select>
                </b-form-group>
            </b-col>
            <b-col cols="3" class="mx-auto">
                <b-form-group label="Scenario 2" label-align="left" class="mt-1" align-self="end">
                    <b-form-select
                        :value="getTimeStringFromScenario('compareScenario2')"
                        :options="getCompareOptionsTimes"
                        size="md"
                        @input="
                            getCompareScenario('compareScenario2', $event);
                        "
                        :disabled="getCompareOptions.length <= 1"
                        data-test="compareSelect2"
                    ></b-form-select>
                </b-form-group>
            </b-col>
        </b-row>
        <transition name="slide-in" mode="out-in">
            <b-row align-h="center" class="mb-3 mt--3" v-if="showInputs">
                <span :class="`ml-5 mr-2`"><i class="fas fa-square-full fa-xs text-different"></i></span> Value changed
                <span :class="`mr-2 ml-5`"><i class="fas fa-square-full fa-xs text-new"></i></span> Exists in only one scenario
            </b-row>
        </transition>

        <b-row class="border-top pt-2">
            <transition name="slide-in" mode="out-in">
                <b-col v-if="loading1" class="py-5 text-center">
                    <v-progress-circular indeterminate size="72" width="6" color="#40C4FF"></v-progress-circular>
                </b-col>
                <CompareContent
                    scenarioName="compareScenario1"
                    :scenario="reorderedScenarios.newScenario1"
                    :showInputs="showInputs"
                    :border="true"
                    v-else-if="getCompareOptions.length > 0"
                    :key="`${getScenarioByName('compareScenario1').id} ${showInputs} 1`"
                />
                <b-col v-else class="border-right text-center" cols="6">
                    There must be at least one saved scenario for Scenario 1 selection.
                </b-col>
            </transition>
            <transition name="slide-in" mode="out-in">
                <b-col v-if="loading2" class="py-5 text-center">
                    <v-progress-circular indeterminate size="72" width="6" color="#40C4FF"></v-progress-circular>
                </b-col>
                <CompareContent
                    scenarioName="compareScenario2"
                    :scenario="reorderedScenarios.newScenario2"
                    :showInputs="showInputs"
                    :border="false"
                    v-else-if="getCompareOptions.length > 1"
                    :key="`${getScenarioByName('compareScenario2').id} ${showInputs} 2`"
                />
                <b-col v-else class="text-center" cols="6">
                    There must be at least two saved scenarios for Scenario 2 selection.
                </b-col>
            </transition>
        </b-row>
    </b-card>
</template>

<script>
import CompareContent from "../components/Compare/CompareContent";
import { mapGetters, mapActions, mapMutations, mapState } from "vuex";
import { reorderFields } from '@/utils/ReorderFields';


export default {
    components: {
        CompareContent,
    },
    data() {
        return {
            loading1: false,
            loading2: false,
            showInputs: false,
        };
    },
    computed: {
        ...mapGetters([
            "getScenario",
            "getCompareOptions",
            "getCompareOptionsTimes",
            "getScenarioByName",
            "getTimeStringFromScenario",
            "getSelectedProgram"
        ]),
        ...mapState(["selectedLoanId"]),
        reorderedScenarios(){
            // reorders the fields to have fields that belong to both scenarios at the beginning and new fields at the end
            // also assigns a status to each field which is used for highlighting the field when displaying if needed
            let newScenario1;
            let newScenario2;

            // gets compareScenario1 from the store and then finds the fields relevant to the program of the selected program
            const scenario1 = this.getScenarioByName('compareScenario1');
            let selectedFieldsS1 = scenario1.fields.filter(f => f.programs.includes(this.getSelectedProgram('compareScenario1')) || f.name === 'Program');

            // gets compareScenario1 from the store and then finds the fields relevant to the program of the selected program
            const scenario2 = this.getScenarioByName('compareScenario2');
            let selectedFieldsS2 = scenario2.fields.filter(f => f.programs.includes(this.getSelectedProgram('compareScenario2')) || f.name === 'Program');

            if ((scenario1.id && scenario2.id)) {
                // finds the differences in the two scenarios only if both have scenarios selected
                let reordered = reorderFields(selectedFieldsS1, selectedFieldsS2); // returns { fieldsS1, fieldsS2 } where each value is the reordered fields for that number scenario

                // make copies of the two scenarios so there is not an infinite render loop
                newScenario1 = JSON.parse(JSON.stringify(scenario1));
                newScenario2 = JSON.parse(JSON.stringify(scenario2));

                // set the fields of each scenario to be the matched fields followed by the unmatched fields
                newScenario1.fields = reordered.fieldsS1;
                newScenario2.fields = reordered.fieldsS2;
            } else if (scenario1.id) {
                newScenario1 = JSON.parse(JSON.stringify(scenario1));
            }
            return { newScenario1, newScenario2 };
        }
    },

    methods: {
        ...mapActions(["fetchScenario", "fetchScenarioTimes"]),
        ...mapMutations(["setSelectedLoanId","reorderFields"]),

        async getCompareScenario(scenarioName, ev) {
            scenarioName === 'compareScenario1' ? this.loading1 = true : this.loading2 = true
            try{
                await this.fetchScenario({ scenario: scenarioName, created: ev })
            } catch (err) {
                console.log(err);
            } finally {
                scenarioName === 'compareScenario1' ? this.loading1 = false : this.loading2 = false
            }
        }
    },
    async mounted() {
        this.setSelectedLoanId(this.$route.params.id);
        this.loading1 = true;
        this.loading2 = true;
        await this.fetchScenarioTimes();
        if (this.getCompareOptions.length > 1) {
            await this.fetchScenario({ scenario: "compareScenario1", created: this.getCompareOptionsTimes[0] });
            this.loading1 = false;
            await this.fetchScenario({ scenario: "compareScenario2", created: this.getCompareOptionsTimes[1] });
            this.loading2 = false;
        } else if (this.getCompareOptions.length > 0) {
            await this.fetchScenario({ scenario: "compareScenario1", created: this.getCompareOptionsTimes[0] });
            this.loading1 = false;
        }
        if (this.getScenario.id === null) {
            await this.setSelectedLoanId(this.$route.params.id);
            await this.fetchScenario({ scenario: "haloScenario" });
        }
        // ensures that spinners are removed
        this.loading1 = false;
        this.loading2 = false;
    },
};
</script>

<style>
.alert {
    width: 99%;
    margin: auto;
    white-space: pre-wrap;
}

.warning {
    color: red;
    font-size: 18;
    font-weight: bold;
}

.spacing {
    margin-left: 10px;
}

.selected {
    font-weight: bold;
}

.text-different {
    color: rgb(0, 221, 255, .35) !important;
    background-color: rgb(0, 221, 255, .35) !important;
    border: 1px solid;
    border-color: black;
    box-sizing:border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
}

.text-new {
    color: rgba(0, 100, 255, 0.25) !important;
    background-color: rgba(0, 100, 255, 0.25) !important;
    border: 1px solid;
    border-color: black;
}
</style>